import { SprintAPI } from '../../utils/api';
import { findNestedChildren, logAudit, clearSelectionIfNeeded } from './utils';

/**
 * Permanently delete a sprint (from trash).
 * Also permanently deletes all nested children to prevent orphans.
 */
export async function permanentlyDeleteSprint({
  sprintId,
  sprints,
  setSprints,
  selectedSprintId,
  selectedTemplateId,
  setSelectedSprintId,
  setSelectedTemplateId,
  setCurrentView,
  VIEWS,
}) {
  const sprintToDelete = sprints.find((s) => s.id === sprintId);
  const nestedChildren = findNestedChildren(sprints, sprintId);
  const allIdsToDelete = [sprintId, ...nestedChildren.map((c) => c.id)];

  console.log('[TRASH] Permanently deleting sprint:', {
    sprintId,
    sprintName: sprintToDelete?.name,
    nestedChildrenCount: nestedChildren.length,
    nestedChildren: nestedChildren.map((c) => c.name),
  });

  logAudit('SPRINT_PERMANENTLY_DELETED', {
    sprintId,
    sprintName: sprintToDelete?.name,
    nestedChildrenDeleted: nestedChildren.length,
  }, sprintId);

  setSprints((prev) => prev.filter((s) => !allIdsToDelete.includes(s.id)));

  clearSelectionIfNeeded({
    affectedIds: allIdsToDelete,
    selectedSprintId,
    selectedTemplateId,
    setSelectedSprintId,
    setSelectedTemplateId,
    setCurrentView,
    VIEWS,
  });

  for (const id of allIdsToDelete) {
    try {
      await SprintAPI.delete(id);
      console.log(
        '✅ Sprint permanently deleted:',
        id === sprintId ? sprintToDelete?.name : nestedChildren.find((c) => c.id === id)?.name
      );
    } catch (error) {
      console.error('Failed to permanently delete sprint:', id, error);
    }
  }
}

/** Empty trash (permanently delete all trashed sprints and their nested children). */
export async function emptyTrash({
  sprints,
  setSprints,
  selectedSprintId,
  selectedTemplateId,
  setSelectedSprintId,
  setSelectedTemplateId,
  setCurrentView,
  VIEWS,
}) {
  const trashedSprints = sprints.filter((s) => s.deletedAt);

  const allIdsToDelete = new Set();
  for (const trashed of trashedSprints) {
    allIdsToDelete.add(trashed.id);
    findNestedChildren(sprints, trashed.id).forEach((c) => allIdsToDelete.add(c.id));
  }

  console.log('[TRASH] Emptying trash:', {
    trashedCount: trashedSprints.length,
    totalToDelete: allIdsToDelete.size,
  });

  logAudit('TRASH_EMPTIED', {
    count: allIdsToDelete.size,
    sprintIds: [...allIdsToDelete],
  });

  setSprints((prev) => prev.filter((s) => !allIdsToDelete.has(s.id)));

  clearSelectionIfNeeded({
    affectedIds: allIdsToDelete,
    selectedSprintId,
    selectedTemplateId,
    setSelectedSprintId,
    setSelectedTemplateId,
    setCurrentView,
    VIEWS,
  });

  for (const id of allIdsToDelete) {
    try {
      await SprintAPI.delete(id);
    } catch (error) {
      console.error('Failed to delete sprint:', id, error);
    }
  }

  console.log('✅ Trash emptied');
}
